// ---------------------------------------------------------------
// Per-alert-type SOUND preference (owner-level, applies to all cars).
// Every alert still pops a banner and plays an attention chime. On top of that,
// the owner can choose which alert types are also SPOKEN out loud (voice).
//
// Default: everything is just a chime, EXCEPT the safety-critical ones —
// crash, tow-away, signal jamming, over-speed, speeding, and check-engine —
// which are spoken by default. The owner can flip any type either way.
// ---------------------------------------------------------------

const KEY = 'ta-alert-sound';

// The list shown in the settings panel (most important first).
export const ALERT_SOUND_CATEGORIES = [
  { key: 'crash', label: 'Crash / impact' },
  { key: 'towTheft', label: 'Tow-away / theft' },
  { key: 'jamming', label: 'Signal jamming' },
  { key: 'overspeed', label: 'Over-speed (hard limit)' },
  { key: 'speeding', label: 'Speeding warning' },
  { key: 'checkEngine', label: 'Check-engine code' },
  { key: 'highwayStop', label: 'Stopped on the highway' },
  { key: 'tampering', label: 'Device tampering' },
  { key: 'offline', label: 'Tracker unplugged / power cut' },
  { key: 'battery', label: 'Low battery' },
  { key: 'lowFuel', label: 'Low fuel' },
  { key: 'idle', label: 'Idling too long' },
  { key: 'driving', label: 'Harsh driving (brake / accel)' },
  { key: 'arrival', label: 'Arrivals / departures' },
  { key: 'ignition', label: 'Car turned on / off' },
  { key: 'arrivedSafe', label: 'Arrived safe' },
  { key: 'curfew', label: 'Curfew' },
  { key: 'watch', label: 'Watch alerts' },
  { key: 'newVehicle', label: 'Tracker moved to a new car' },
  { key: 'other', label: 'Other alerts' },
];

// Spoken by default. Everything else just chimes.
export const VOICE_DEFAULTS = new Set(['crash', 'towTheft', 'jamming', 'overspeed', 'speeding', 'checkEngine']);

// Map a runtime alert to one of the categories above — by its key prefix first,
// then by icon as a fallback.
export function alertCategory(al = {}) {
  const key = String(al.key || '');
  const pre = key.split(':')[0];
  const byPrefix = {
    ovspd: 'overspeed', spd: 'speeding', dtc: 'checkEngine', tow: 'towTheft', jam: 'jamming',
    hwystop: 'highwayStop', disc: 'offline', powercut: 'offline', devbatt10: 'offline', devbatt50: 'offline',
    lowbatt: 'battery', lowfuel: 'lowFuel', lowrange: 'lowFuel', idle: 'idle',
    place: 'arrival', ignon: 'ignition', ignoff: 'ignition', arrsafe: 'arrivedSafe',
    curfewhome: 'curfew', curfewout: 'curfew', curfewsafe: 'curfew',
    watch: 'watch', watchdone: 'watch', newveh: 'newVehicle',
  };
  if (byPrefix[pre]) return byPrefix[pre];
  if (pre === 'evt') {
    if (al.icon === '💥') return 'crash';
    if (al.icon === '🪝') return 'towTheft';
    if (al.icon === '📡') return 'jamming';
    if (al.icon === '🔌') return 'offline';
    if (al.icon === '🔧') return 'tampering';
    return 'driving';
  }
  // icon fallbacks for anything unkeyed
  if (al.icon === '💥') return 'crash';
  if (al.icon === '🪝') return 'towTheft';
  if (al.icon === '📡') return 'jamming';
  if (al.icon === '🛠️') return 'checkEngine';
  return 'other';
}

export function getSoundPrefs() {
  let saved = {};
  try { saved = JSON.parse(localStorage.getItem(KEY)) || {}; } catch { /* ignore */ }
  const out = {};
  ALERT_SOUND_CATEGORIES.forEach((c) => {
    out[c.key] = (saved[c.key] === 'voice' || saved[c.key] === 'chime')
      ? saved[c.key]
      : (VOICE_DEFAULTS.has(c.key) ? 'voice' : 'chime');
  });
  return out;
}

export function saveSoundPrefs(prefs) {
  try { localStorage.setItem(KEY, JSON.stringify(prefs)); } catch { /* ignore */ }
}

// 'voice' → chime + spoken; 'chime' → chime only.
export function soundForAlert(al) {
  const c = alertCategory(al);
  const p = getSoundPrefs();
  return p[c] || (VOICE_DEFAULTS.has(c) ? 'voice' : 'chime');
}
